/**
 * Detailed product mockups for feature pages.
 * These are HTML/CSS-based realistic UI screenshots that mimic the actual
 * Rezaro admin and customer-facing pages.
 */

import { Calendar, Clock, Phone, Users, Check, AlertCircle, Star, ShoppingBag, Plus, Bell, MapPin, CreditCard, Wallet, ChevronRight, MessageSquare, Camera, Search } from 'lucide-react'

// Browser-like wrapper that wraps mockups to look like a Mac window
export function BrowserFrame({ children, label }: { children: React.ReactNode; label?: string }) {
  return (
    <div className="relative rounded-2xl border border-pub-rule bg-pub-surface shadow-[0_30px_70px_-20px_rgba(23,106,71,0.25)] overflow-hidden">
      <div className="flex items-center gap-2 border-b border-pub-rule bg-pub-bg/50 px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-yellow-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-green-300" />
        {label && (
          <div className="ml-3 flex-1 min-w-0 max-w-xs">
            <div className="truncate rounded-md bg-pub-rule px-3 py-0.5 text-[10px] font-medium text-pub-muted">
              {label}
            </div>
          </div>
        )}
      </div>
      {/* Detailed product mockups have a natural width (~415px) that's
          wider than a small phone. Rather than clip them (Sa/So columns
          and badges were getting cut off), let the content scroll
          horizontally inside the frame on narrow screens. */}
      <div className="overflow-x-auto scrollbar-none">
        {children}
      </div>
    </div>
  )
}

// Phone-like wrapper
export function PhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative mx-auto" style={{ width: 280 }}>
      <div
        className="rounded-[2.5rem] p-[3px] shadow-[0_30px_70px_-20px_rgba(23,106,71,0.4)]"
        style={{ background: 'linear-gradient(140deg, #2A2240 0%, #4D3F73 50%, #2A2240 100%)' }}
      >
        <div className="rounded-[2.4rem] p-1.5" style={{ background: '#1A1530' }}>
          <div className="relative rounded-[2rem] bg-pub-surface overflow-hidden" style={{ aspectRatio: '9/19' }}>
            <div className="absolute left-1/2 top-2 -translate-x-1/2 z-10 h-5 w-24 rounded-full"
              style={{ background: '#0E0A1F' }} />
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// 1. Reservation day book (Tagesübersicht)
// ============================================================
export function MockReservationBook() {
  const bookings = [
    { time: '18:00', name: 'Familie Müller', guests: 4, table: 'T3', status: 'confirmed', notes: 'Geburtstag' },
    { time: '18:30', name: 'A. Schneider', guests: 2, table: 'T1', status: 'confirmed' },
    { time: '19:00', name: 'M. Weber', guests: 6, table: 'T7+T8', status: 'pending', notes: 'Allergie: Nüsse' },
    { time: '19:30', name: 'L. Koch', guests: 3, table: 'T4', status: 'confirmed' },
    { time: '20:00', name: 'Familie Yılmaz', guests: 5, table: 'T9', status: 'walkin' },
    { time: '20:30', name: 'C. Becker', guests: 2, table: 'T2', status: 'confirmed' },
  ]
  const statusStyle: Record<string, string> = {
    confirmed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    pending: 'bg-amber-100 text-amber-700 border-amber-200',
    walkin: 'bg-blue-100 text-blue-700 border-blue-200',
  }
  const statusLabel: Record<string, string> = {
    confirmed: 'Bestätigt',
    pending: 'Ausstehend',
    walkin: 'Walk-in',
  }
  return (
    <BrowserFrame label="rezaro.de/restaurant/bookings/day">
      <div className="bg-pub-bg p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-pub-muted">Heute · Sa, 17. Mai</p>
            <p className="text-[18px] font-semibold text-pub-ink">Reservierungen</p>
          </div>
          <div className="flex gap-2">
            <div className="rounded-lg border border-pub-rule bg-pub-surface px-3 py-1.5 text-[11px] font-semibold text-pub-ink">
              <Users className="inline h-3 w-3 mr-1" />28 Gäste
            </div>
            <div className="rounded-lg bg-pub-accent px-3 py-1.5 text-[11px] font-semibold text-white">
              <Plus className="inline h-3 w-3 mr-1" />Reservierung
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-pub-rule bg-pub-surface overflow-hidden">
          {bookings.map((b, i) => (
            <div key={i} className={`flex items-center gap-3 px-4 py-2.5 ${i < bookings.length - 1 ? 'border-b border-pub-rule' : ''}`}>
              <div className="flex flex-col items-center justify-center w-12">
                <span className="text-[14px] font-semibold text-pub-ink">{b.time}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-pub-ink truncate">{b.name}</p>
                {b.notes && <p className="text-[10px] text-pub-muted truncate">📝 {b.notes}</p>}
              </div>
              <div className="flex items-center gap-1 text-[11px] text-pub-muted">
                <Users className="h-3 w-3" />{b.guests}
              </div>
              <div className="text-[11px] font-mono text-pub-muted w-14 text-right">{b.table}</div>
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusStyle[b.status]}`}>
                {statusLabel[b.status]}
              </span>
            </div>
          ))}
        </div>
      </div>
    </BrowserFrame>
  )
}

// ============================================================
// 2. Booking widget (mobile-style)
// ============================================================
export function MockBookingWidget() {
  return (
    <PhoneFrame>
      <div className="h-full bg-neutral-50 pt-8">
        <div className="bg-white border-b border-neutral-100 px-4 py-3 text-center">
          <p className="text-[13px] font-bold text-neutral-900">Ristorante Bella</p>
          <p className="text-[8px] uppercase tracking-widest text-neutral-400">Online-Reservierung</p>
        </div>
        <div className="flex border-b border-neutral-100">
          <div className="flex-1 border-r border-neutral-100 px-3 py-2.5 text-center text-[10px] font-medium text-neutral-700">
            4 Personen
          </div>
          <div className="flex-1 px-3 py-2.5 text-center text-[10px] font-medium text-neutral-700">
            Heute
          </div>
        </div>
        <div className="p-3">
          <div className="grid grid-cols-3 gap-1.5">
            {['18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00'].map((t, i) => (
              <div key={t} className={`rounded-lg border py-1.5 text-center text-[10px] font-medium ${i === 4 ? 'border-pub-accent bg-pub-accent text-white' : 'border-neutral-200 text-neutral-700'}`}>
                {t}
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-lg bg-pub-accent py-2.5 text-center text-[11px] font-semibold text-white">
            Weiter
          </div>
          <p className="mt-3 text-center text-[8px] text-neutral-400">
            Powered by <span className="font-semibold">Rezaro</span>
          </p>
        </div>
      </div>
    </PhoneFrame>
  )
}

// ============================================================
// 3. Order management (admin)
// ============================================================
export function MockOrders() {
  const orders = [
    { num: 1042, type: 'Lieferung', name: 'M. Schmidt', items: 3, total: '34,80 €', status: 'new', mins: 2 },
    { num: 1041, type: 'Abholung', name: 'A. Wagner', items: 2, total: '21,50 €', status: 'preparing', mins: 8 },
    { num: 1040, type: 'Vor Ort', name: 'Tisch 5', items: 6, total: '78,90 €', status: 'ready', mins: 15 },
  ]
  const statusStyle: Record<string, { bg: string; label: string }> = {
    new: { bg: 'bg-amber-100 text-amber-700', label: 'NEU' },
    preparing: { bg: 'bg-brand-100 text-brand-700', label: 'IN ZUBEREITUNG' },
    ready: { bg: 'bg-emerald-100 text-emerald-700', label: 'BEREIT' },
  }
  return (
    <BrowserFrame label="rezaro.de/restaurant/orders">
      <div className="bg-pub-bg p-5">
        <p className="text-[11px] uppercase tracking-widest text-pub-muted mb-1">Heute · Live</p>
        <p className="text-[18px] font-semibold text-pub-ink mb-4">Bestellungen</p>
        <div className="grid grid-cols-3 gap-3">
          {orders.map((o) => (
            <div key={o.num} className="rounded-xl border border-pub-rule bg-pub-surface p-3">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-[11px] font-semibold text-pub-ink">#{o.num}</p>
                  <p className="text-[9px] text-pub-muted flex items-center gap-1">
                    <Clock className="h-2.5 w-2.5" />{o.mins} min · {o.type}
                  </p>
                </div>
                <span className={`px-1.5 py-0.5 rounded-full text-[7px] font-bold ${statusStyle[o.status].bg}`}>
                  {statusStyle[o.status].label}
                </span>
              </div>
              <p className="text-[11px] font-medium text-pub-ink truncate">{o.name}</p>
              <p className="text-[9px] text-pub-muted mb-2">{o.items} Artikel</p>
              <div className="border-t border-pub-rule pt-2 flex items-center justify-between">
                <span className="text-[12px] font-bold text-pub-ink">{o.total}</span>
                <ChevronRight className="h-3 w-3 text-pub-muted" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </BrowserFrame>
  )
}

// ============================================================
// 4. QR menu / customer-facing
// ============================================================
export function MockQRMenu() {
  return (
    <PhoneFrame>
      <div className="h-full bg-[#fafaf7] pt-8">
        <div className="relative h-20 bg-gradient-to-br from-brand-400 to-brand-900">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/40" />
          <div className="absolute bottom-2 left-3 right-3">
            <p className="text-[7px] uppercase tracking-[0.25em] text-white/70">Speisekarte</p>
            <p className="text-[14px] font-semibold text-white" style={{ fontFamily: 'serif' }}>Ristorante Bella</p>
          </div>
        </div>
        <div className="flex gap-1.5 px-3 py-2 overflow-hidden">
          {['Vorspeisen', 'Pasta', 'Hauptgang', 'Dessert'].map((c, i) => (
            <span key={c} className={`rounded-full border px-2.5 py-0.5 text-[8px] font-medium whitespace-nowrap ${i === 0 ? 'bg-brand-700 border-brand-700 text-white' : 'bg-white border-neutral-200 text-neutral-700'}`}>
              {c}
            </span>
          ))}
        </div>
        <div className="px-3 space-y-2">
          {[
            { name: 'Bruschetta al Pomodoro', price: '8,50 €', tags: ['🌱'] },
            { name: 'Carpaccio di Manzo', price: '14,00 €', tags: [] },
            { name: 'Spaghetti Carbonara', price: '13,50 €', tags: ['⭐'] },
          ].map((item, i) => (
            <div key={i} className="flex items-start bg-white rounded-xl border border-neutral-100 overflow-hidden">
              <div className="h-12 w-12 flex-shrink-0 bg-gradient-to-br from-amber-100 to-orange-200" />
              <div className="flex-1 p-2 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-1 min-w-0">
                    <p className="text-[10px] font-semibold text-neutral-900 truncate">{item.name}</p>
                    {item.tags.map((t, j) => <span key={j} className="text-[9px]">{t}</span>)}
                  </div>
                  <span className="text-[10px] font-semibold text-brand-700 flex-shrink-0 ml-1" style={{ fontFamily: 'serif' }}>{item.price}</span>
                </div>
                <div className="flex justify-end mt-1">
                  <span className="rounded-full bg-brand-700 px-2 py-0.5 text-[8px] font-semibold text-white">+ Hinzufügen</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PhoneFrame>
  )
}

// ============================================================
// 5. Floor plan (Tischplan) — mirrors admin /bookings/day FloorPlanView
// ============================================================
type TableShape = 'rect' | 'round' | 'wide'
type TableStatus = 'free' | 'occupied' | 'reserved'

function ChairDots({ shape, capacity, w, h }: { shape: TableShape; capacity: number; w: number; h: number }) {
  const gap = 4
  const size = 6
  const positions: { x: number; y: number }[] = []
  if (shape === 'round') {
    const r = w / 2 + gap + size / 2
    for (let i = 0; i < capacity; i++) {
      const a = (i / capacity) * 2 * Math.PI - Math.PI / 2
      positions.push({ x: Math.cos(a) * r, y: Math.sin(a) * r })
    }
  } else if (shape === 'wide') {
    const top = Math.ceil(capacity / 2)
    const bot = Math.floor(capacity / 2)
    const yT = -(h / 2 + gap + size / 2)
    const yB = h / 2 + gap + size / 2
    for (let i = 0; i < top; i++) positions.push({ x: ((i + 1) * w / (top + 1)) - w / 2, y: yT })
    for (let i = 0; i < bot; i++) positions.push({ x: ((i + 1) * w / (bot + 1)) - w / 2, y: yB })
  } else {
    const off = gap + size / 2
    positions.push({ x: -w / 4, y: -(h / 2 + off) })
    positions.push({ x: w / 4, y: -(h / 2 + off) })
    positions.push({ x: -w / 4, y: h / 2 + off })
    positions.push({ x: w / 4, y: h / 2 + off })
    if (capacity >= 5) positions.push({ x: -(w / 2 + off), y: 0 })
    if (capacity >= 6) positions.push({ x: w / 2 + off, y: 0 })
  }
  return (
    <>
      {positions.slice(0, capacity).map((p, i) => (
        <div key={i}
          className="absolute rounded-full bg-neutral-300"
          style={{
            width: size, height: size,
            left: `calc(50% + ${p.x}px - ${size / 2}px)`,
            top: `calc(50% + ${p.y}px - ${size / 2}px)`,
          }}
        />
      ))}
    </>
  )
}

export function MockFloorPlan() {
  const PLAN_W = 540
  const PLAN_H = 320
  const tables: { x: number; y: number; w: number; h: number; shape: TableShape; label: string; status: TableStatus; capacity: number }[] = [
    { x: 80,  y: 60,  w: 56, h: 56, shape: 'round', label: 'T1', status: 'free',     capacity: 4 },
    { x: 200, y: 60,  w: 96, h: 48, shape: 'wide',  label: 'T2', status: 'occupied', capacity: 6 },
    { x: 360, y: 60,  w: 56, h: 56, shape: 'round', label: 'T3', status: 'reserved', capacity: 4 },
    { x: 470, y: 75,  w: 48, h: 48, shape: 'rect',  label: 'T4', status: 'free',     capacity: 2 },
    { x: 80,  y: 180, w: 56, h: 56, shape: 'round', label: 'T5', status: 'occupied', capacity: 4 },
    { x: 220, y: 180, w: 56, h: 56, shape: 'rect',  label: 'T6', status: 'reserved', capacity: 4 },
    { x: 360, y: 175, w: 96, h: 48, shape: 'wide',  label: 'T7+8', status: 'reserved', capacity: 8 },
    { x: 130, y: 270, w: 56, h: 56, shape: 'round', label: 'T9', status: 'free',     capacity: 4 },
    { x: 280, y: 270, w: 56, h: 56, shape: 'round', label: 'T10', status: 'free',    capacity: 4 },
    { x: 410, y: 265, w: 64, h: 56, shape: 'rect',  label: 'T11', status: 'occupied', capacity: 4 },
  ]
  const statusFill: Record<TableStatus, string> = {
    free:     '#FFFFFF',
    occupied: '#DCFCE7',
    reserved: '#FEF3C7',
  }
  const statusBorder: Record<TableStatus, string> = {
    free:     '#D4D4D8',
    occupied: '#22C55E',
    reserved: '#F59E0B',
  }
  const statusText: Record<TableStatus, string> = {
    free:     '#52525B',
    occupied: '#15803D',
    reserved: '#B45309',
  }

  return (
    <BrowserFrame label="rezaro.de/bistro-rosa/bookings/day">
      <div className="bg-pub-bg p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-pub-muted">Heute · Sa 17. Mai · 19:42</p>
            <p className="text-[14px] font-semibold text-pub-ink">Tischplan · Hauptsaal</p>
          </div>
          <div className="flex gap-1 text-[9px]">
            <span className="flex items-center gap-1 rounded-full border border-pub-rule bg-white px-2 py-0.5"><span className="h-1.5 w-1.5 rounded-full bg-white border border-neutral-300" />Frei</span>
            <span className="flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />Anwesend</span>
            <span className="flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" />Reserviert</span>
          </div>
        </div>

        {/* Floor area with grid */}
        <div className="relative rounded-xl border-2 border-dashed border-pub-rule bg-neutral-50/60 overflow-hidden"
          style={{ width: '100%', aspectRatio: `${PLAN_W}/${PLAN_H}` }}>
          {/* Dot grid */}
          <svg className="absolute inset-0 w-full h-full" viewBox={`0 0 ${PLAN_W} ${PLAN_H}`} preserveAspectRatio="none">
            {Array.from({ length: Math.ceil(PLAN_W / 20) }, (_, i) =>
              Array.from({ length: Math.ceil(PLAN_H / 20) }, (_, j) => (
                <circle key={`${i}-${j}`} cx={i * 20} cy={j * 20} r={0.8} fill="#D4D4D8" />
              )),
            )}
          </svg>

          {/* Wall hints */}
          <div className="absolute inset-2 border border-neutral-200 rounded-md pointer-events-none" />

          {/* Tables */}
          <div className="absolute inset-0">
            {tables.map((t) => {
              const widthPct = (t.w / PLAN_W) * 100
              const heightPct = (t.h / PLAN_H) * 100
              const leftPct = (t.x / PLAN_W) * 100
              const topPct = (t.y / PLAN_H) * 100
              return (
                <div key={t.label}
                  className="absolute"
                  style={{
                    left: `${leftPct}%`, top: `${topPct}%`,
                    width: `${widthPct}%`, height: `${heightPct}%`,
                  }}>
                  <ChairDots shape={t.shape} capacity={t.capacity} w={t.w} h={t.h} />
                  <div
                    className="absolute inset-0 flex items-center justify-center text-[8px] font-bold tabular-nums shadow-sm"
                    style={{
                      background: statusFill[t.status],
                      border: `1.5px solid ${statusBorder[t.status]}`,
                      color: statusText[t.status],
                      borderRadius: t.shape === 'round' ? '50%' : 6,
                    }}>
                    {t.label}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-3 grid grid-cols-4 gap-2">
          {[
            { label: 'Plätze', v: '46' },
            { label: 'Belegt',     v: '24' },
            { label: 'Auslastung', v: '52 %' },
            { label: 'Walk-ins',   v: '+3' },
          ].map((s) => (
            <div key={s.label} className="rounded-lg border border-pub-rule bg-white px-2 py-1.5 text-center">
              <p className="text-[9px] text-pub-muted">{s.label}</p>
              <p className="text-[12.5px] font-bold text-pub-ink tabular-nums">{s.v}</p>
            </div>
          ))}
        </div>
      </div>
    </BrowserFrame>
  )
}

// ============================================================
// 6. Booking settings / smart confirm
// ============================================================
export function MockSettings() {
  return (
    <BrowserFrame label="rezaro.de/restaurant/settings/bookings">
      <div className="bg-pub-bg p-5">
        <p className="text-[14px] font-semibold text-pub-ink mb-4">Reservierungseinstellungen</p>

        <div className="rounded-xl bg-pub-surface border border-pub-rule p-4 mb-3">
          <p className="text-[10px] uppercase tracking-widest text-pub-muted mb-2">Bestätigungsmodus</p>
          <div className="space-y-1.5">
            {[
              { label: 'Automatisch bestätigen', selected: false },
              { label: 'Bis zu 6 Personen automatisch', selected: true },
              { label: 'Manuell bestätigen', selected: false },
            ].map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className={`h-3 w-3 rounded-full border-2 ${opt.selected ? 'border-pub-accent bg-pub-accent' : 'border-pub-rule'}`}>
                  {opt.selected && <div className="h-full w-full rounded-full bg-white scale-50" />}
                </div>
                <span className="text-[11px] text-pub-ink">{opt.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl bg-pub-surface border border-pub-rule p-4">
          <div className="space-y-2.5">
            {[
              { label: 'Reservierungs-Erinnerungen senden', on: true },
              { label: 'Antwortoption für Gäste', on: true },
              { label: 'Sicherheitscode-Verifizierung', on: false },
            ].map((s, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-[11px] text-pub-ink">{s.label}</span>
                <div className={`relative w-7 h-3.5 rounded-full ${s.on ? 'bg-pub-accent' : 'bg-pub-rule'}`}>
                  <div className={`absolute top-0.5 h-2.5 w-2.5 rounded-full bg-white shadow ${s.on ? 'right-0.5' : 'left-0.5'}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </BrowserFrame>
  )
}

// ============================================================
// 7. Restaurant website preview — mirrors actual /(public)/[restaurant]/page.tsx render
// ============================================================
export function MockRestaurantWebsite() {
  return (
    <BrowserFrame label="rezaro.de/bistro-rosa">
      <div className="bg-white">
        {/* Top nav strip */}
        <div className="flex items-center justify-between px-4 py-2 text-[9px] border-b border-neutral-100">
          <span className="font-bold tracking-wide text-neutral-900" style={{ fontFamily: 'var(--font-display, serif)', fontStyle: 'italic' }}>
            Bistro Rosa
          </span>
          <div className="flex gap-3 text-neutral-500">
            <span>Speisekarte</span>
            <span>Über uns</span>
            <span>Kontakt</span>
            <span className="rounded-full bg-brand-700 px-2 py-0.5 text-white">Reservieren</span>
          </div>
        </div>

        {/* Hero with overlay text */}
        <div className="relative h-40 overflow-hidden">
          <div className="absolute inset-0"
            style={{
              // Editorial deep forest green sweep with warm cream highlights
              // — replaces the multi-stop rainbow gradient.
              background: 'linear-gradient(135deg, #0a1f17 0%, #1a3a2e 40%, #2a4d3e 70%, #c2410c 100%)',
            }} />
          {/* Subtle texture */}
          <div className="absolute inset-0 opacity-25"
            style={{
              backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(0,0,0,0.3) 0%, transparent 50%)',
            }} />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/55" />
          <div className="absolute bottom-3 left-4 right-4">
            <p className="text-[8px] uppercase tracking-[0.2em] text-white/75">Italienisch · Mitte</p>
            <p className="mt-0.5 text-[22px] leading-tight text-white" style={{ fontFamily: 'serif', fontWeight: 600 }}>
              Bistro <em style={{ fontStyle: 'italic' }}>Rosa</em>
            </p>
            <p className="mt-0.5 text-[9px] text-white/90 flex items-center gap-1">
              <MapPin className="h-2.5 w-2.5" />Friedrichstr. 142, 10117 Berlin · 4,9 ★ (128)
            </p>
          </div>
        </div>

        {/* CTA bar */}
        <div className="flex gap-2 px-4 py-3 border-b border-neutral-100">
          <button className="flex-1 rounded-full bg-brand-700 py-2 text-[10.5px] font-semibold text-white shadow-md">
            Tisch reservieren
          </button>
          <button className="rounded-full border border-neutral-300 px-3.5 py-2 text-[10.5px] font-semibold text-neutral-700">
            Speisekarte ↓
          </button>
        </div>

        {/* Highlights row */}
        <div className="grid grid-cols-3 gap-1 px-4 py-3 border-b border-neutral-100">
          {[
            { label: 'Öffnungszeiten', value: 'Heute geöffnet' },
            { label: 'Reservierung', value: 'Sofort bestätigt' },
            { label: 'Menü', value: '32 Gerichte' },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <p className="text-[7.5px] uppercase tracking-widest text-neutral-400">{s.label}</p>
              <p className="text-[9.5px] font-semibold text-neutral-900 mt-0.5">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Today's special */}
        <div className="px-4 py-3 border-b border-neutral-100">
          <p className="text-[8.5px] uppercase tracking-widest text-brand-700 font-semibold mb-1.5">Tagesempfehlung</p>
          <div className="flex gap-2.5">
            <div className="h-12 w-12 rounded-lg flex-shrink-0 flex items-center justify-center text-2xl"
              style={{ background: 'linear-gradient(135deg, #FFE5D9, #F4A261)' }}>
              🍝
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-neutral-900 leading-tight">Tagliatelle al Tartufo Nero</p>
              <p className="text-[9.5px] text-neutral-500 mt-0.5 line-clamp-2">Frische hausgemachte Tagliatelle, schwarzer Wintertrüffel, Parmesan reggiano DOP.</p>
              <p className="text-[10px] font-bold text-brand-700 mt-1">18,50 €</p>
            </div>
          </div>
        </div>

        {/* Opening hours */}
        <div className="px-4 py-3">
          <p className="text-[8.5px] uppercase tracking-widest text-neutral-400 mb-1.5">Öffnungszeiten</p>
          <div className="space-y-0.5 text-[9.5px]">
            {[
              { day: 'Mo – Fr', hours: '12:00 – 23:00', today: false },
              { day: 'Sa', hours: '12:00 – 24:00', today: true },
              { day: 'So', hours: '12:00 – 22:00', today: false },
            ].map((d) => (
              <div key={d.day} className="flex justify-between">
                <span className={d.today ? 'font-semibold text-neutral-900' : 'text-neutral-700'}>
                  {d.day}{d.today && <span className="ml-1 text-brand-700">· heute</span>}
                </span>
                <span className="text-neutral-500 tabular-nums">{d.hours}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </BrowserFrame>
  )
}

// ============================================================
// 8. Reviews / Bewertungen
// ============================================================
export function MockReviews() {
  const reviews = [
    { stars: 5, name: 'Anna M.', date: 'vor 2 Tagen', text: 'Wundervolles Ambiente und das Essen war fantastisch! Kommen wieder.', source: 'Google' },
    { stars: 5, name: 'Thomas K.', date: 'vor 5 Tagen', text: 'Top Service, schnelle Tisch-Zuweisung über Online-Reservierung.', source: 'Google' },
    { stars: 4, name: 'Lisa B.', date: 'vor 1 Woche', text: 'Sehr gut, nur etwas voll am Wochenende.', source: 'Google' },
  ]
  return (
    <BrowserFrame label="rezaro.de/restaurant/feedback">
      <div className="bg-pub-bg p-5">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <p className="text-[14px] font-semibold text-pub-ink">Gastbewertungen</p>
            <div className="flex items-center gap-1 mt-1">
              {[1, 2, 3, 4, 5].map(s => <Star key={s} className="h-4 w-4 fill-amber-400 text-amber-400" />)}
              <span className="ml-1 text-[12px] font-bold text-pub-ink">4.8</span>
              <span className="text-[10px] text-pub-muted">· 247 Bewertungen</span>
            </div>
          </div>
          <div className="rounded-lg bg-emerald-100 px-2 py-1 text-[9px] font-bold text-emerald-700">
            ↑ +0.4 in 90 Tagen
          </div>
        </div>
        <div className="space-y-2">
          {reviews.map((r, i) => (
            <div key={i} className="rounded-xl bg-pub-surface border border-pub-rule p-3">
              <div className="flex items-start justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <div className="h-6 w-6 rounded-full bg-pub-accent-bg flex items-center justify-center text-[9px] font-bold text-pub-accent">
                    {r.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-pub-ink">{r.name}</p>
                    <p className="text-[9px] text-pub-muted">{r.date} · {r.source}</p>
                  </div>
                </div>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star key={s} className={`h-2.5 w-2.5 ${s <= r.stars ? 'fill-amber-400 text-amber-400' : 'text-pub-rule'}`} />
                  ))}
                </div>
              </div>
              <p className="text-[10px] text-pub-muted leading-relaxed">{r.text}</p>
            </div>
          ))}
        </div>
      </div>
    </BrowserFrame>
  )
}

// ============================================================
// 9. Anzahlung / payment
// ============================================================
export function MockDeposit() {
  return (
    <BrowserFrame label="rezaro.de/restaurant/widget">
      <div className="bg-pub-bg p-5">
        <div className="rounded-2xl bg-pub-surface border border-pub-rule p-5 max-w-sm mx-auto">
          <p className="text-[11px] uppercase tracking-widest text-pub-muted mb-1">Reservierung</p>
          <p className="text-[16px] font-semibold text-pub-ink mb-4">Sa, 17.05. · 19:00</p>
          <div className="space-y-2 mb-4 text-[12px]">
            <div className="flex justify-between"><span className="text-pub-muted">Personen</span><span className="text-pub-ink font-semibold">8</span></div>
            <div className="flex justify-between"><span className="text-pub-muted">Anzahlung pro Person</span><span className="text-pub-ink font-semibold">15,00 €</span></div>
            <div className="border-t border-pub-rule pt-2 flex justify-between">
              <span className="text-pub-ink font-semibold">Gesamt</span>
              <span className="text-pub-ink font-bold text-[15px]">120,00 €</span>
            </div>
          </div>
          <div className="rounded-lg border border-pub-rule p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <CreditCard className="h-4 w-4 text-pub-muted" />
              <span className="text-[10px] font-semibold text-pub-muted">VISA •••• 4242</span>
            </div>
            <div className="text-[16px] font-mono tracking-widest text-pub-ink">•••• •••• •••• 4242</div>
          </div>
          <div className="rounded-lg bg-pub-accent py-2.5 text-center text-[11px] font-semibold text-white">
            Anzahlung sichern · 120,00 €
          </div>
          <p className="mt-3 text-[9px] text-pub-muted text-center">
            🔒 Anzahlung erfasst · bei Erscheinen wird sie verrechnet
          </p>
        </div>
      </div>
    </BrowserFrame>
  )
}

// ============================================================
// 10. Customer/Guest list
// ============================================================
export function MockCustomers() {
  const guests = [
    { name: 'Familie Müller', email: 'mueller@example.de', visits: 12, last: 'Heute', status: 'VIP' },
    { name: 'Anna Schneider', email: 'a.schneider@example.de', visits: 8, last: 'vor 3 Tagen', status: '' },
    { name: 'Thomas Weber', email: 't.weber@example.de', visits: 5, last: 'vor 1 Woche', status: '' },
    { name: 'Maria Koch', email: 'm.koch@example.de', visits: 3, last: 'vor 2 Wochen', status: '' },
  ]
  return (
    <BrowserFrame label="rezaro.de/restaurant/customers">
      <div className="bg-pub-bg p-5">
        <div className="flex items-baseline justify-between mb-3">
          <p className="text-[14px] font-semibold text-pub-ink">Gäste</p>
          <p className="text-[10px] text-pub-muted">247 Stammgäste · 1.842 insgesamt</p>
        </div>
        <div className="rounded-xl border border-pub-rule bg-pub-surface overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-pub-bg border-b border-pub-rule text-[9px] font-semibold uppercase tracking-widest text-pub-muted">
            <div className="col-span-5">Name</div>
            <div className="col-span-3">Letzter Besuch</div>
            <div className="col-span-2">Besuche</div>
            <div className="col-span-2">Status</div>
          </div>
          {guests.map((g, i) => (
            <div key={i} className={`grid grid-cols-12 gap-2 px-3 py-2.5 items-center text-[11px] ${i < guests.length - 1 ? 'border-b border-pub-rule' : ''}`}>
              <div className="col-span-5">
                <p className="font-semibold text-pub-ink truncate">{g.name}</p>
                <p className="text-[9px] text-pub-muted truncate">{g.email}</p>
              </div>
              <div className="col-span-3 text-pub-muted text-[10px]">{g.last}</div>
              <div className="col-span-2 font-bold text-pub-ink">{g.visits}</div>
              <div className="col-span-2">
                {g.status === 'VIP' ? (
                  <span className="rounded-full bg-amber-100 text-amber-700 px-1.5 py-0.5 text-[9px] font-bold">VIP</span>
                ) : (
                  <span className="text-pub-muted text-[10px]">Aktiv</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </BrowserFrame>
  )
}

// ============================================================
// 11. Menu admin
// ============================================================
export function MockMenuAdmin() {
  const items = [
    { name: 'Bruschetta al Pomodoro', price: '8,50 €', cat: 'Vorspeisen', emoji: '🥗' },
    { name: 'Spaghetti Carbonara', price: '13,50 €', cat: 'Pasta', emoji: '⭐' },
    { name: 'Tiramisu', price: '6,80 €', cat: 'Dessert', emoji: '' },
    { name: 'Pizza Margherita', price: '11,00 €', cat: 'Pizza', emoji: '🌱' },
  ]
  return (
    <BrowserFrame label="rezaro.de/restaurant/menu">
      <div className="bg-pub-bg p-5">
        <div className="flex items-baseline justify-between mb-3">
          <p className="text-[14px] font-semibold text-pub-ink">Speisekarte</p>
          <div className="rounded-lg bg-pub-accent px-3 py-1.5 text-[10px] font-semibold text-white flex items-center gap-1">
            <Plus className="h-3 w-3" />Gericht
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {items.map((it, i) => (
            <div key={i} className="rounded-xl border border-pub-rule bg-pub-surface overflow-hidden">
              <div className="h-14 bg-gradient-to-br from-amber-100 to-orange-200 flex items-center justify-center text-2xl">
                {it.emoji || '🍽️'}
              </div>
              <div className="p-2">
                <p className="text-[10px] font-semibold text-pub-ink truncate">{it.name}</p>
                <div className="flex items-baseline justify-between mt-1">
                  <span className="text-[8px] text-pub-muted">{it.cat}</span>
                  <span className="text-[11px] font-bold text-pub-accent">{it.price}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </BrowserFrame>
  )
}

// ============================================================
// 12. Widget Code embed
// ============================================================
export function MockWidgetCode() {
  return (
    <div className="space-y-3">
      <BrowserFrame label="Code-Snippet kopieren">
        <div className="bg-pub-dark p-4">
          <pre className="text-[10px] leading-relaxed text-emerald-300 font-mono whitespace-pre">
{`<!-- Rezaro Reservierungs-Widget -->
<iframe
  src="https://rezaro.de/ristorante-bella/widget"
  width="100%"
  height="640"
  frameborder="0"
  style="max-width:480px;border-radius:16px"
></iframe>`}
          </pre>
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1 text-[10px] text-white">
            <Check className="h-3 w-3" />Kopiert
          </div>
        </div>
      </BrowserFrame>
      <div className="rounded-xl border border-pub-rule bg-pub-surface p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded bg-pub-accent-bg flex items-center justify-center text-[8px] font-bold text-pub-accent">
            WP
          </div>
          <span className="text-[10px] text-pub-ink">WordPress, Wix, Squarespace, Webflow…</span>
        </div>
        <Check className="h-3 w-3 text-emerald-500" />
      </div>
    </div>
  )
}

// ============================================================
// 13. Notifications / reminder mockup
// ============================================================
export function MockReminder() {
  return (
    <PhoneFrame>
      <div className="h-full bg-neutral-900 pt-10 px-3">
        <div className="text-center mb-6">
          <p className="text-[8px] text-white/50">Mo, 17. Mai · 13:00</p>
          <p className="text-[10px] font-semibold text-white mt-0.5">Heute</p>
        </div>
        {/* Reminder notification */}
        <div className="rounded-2xl bg-white/95 p-3 backdrop-blur shadow-2xl mb-2">
          <div className="flex items-start gap-2">
            <div className="h-8 w-8 rounded-lg bg-brand-700 flex items-center justify-center flex-shrink-0">
              <Bell className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <p className="text-[9px] font-bold text-neutral-900">REZARO</p>
                <p className="text-[8px] text-neutral-400">jetzt</p>
              </div>
              <p className="text-[10px] font-semibold text-neutral-900 mt-0.5">
                Erinnerung: Reservierung heute 19:00
              </p>
              <p className="text-[9px] text-neutral-600 leading-snug mt-0.5">
                Ristorante Bella · 4 Personen · Antworten Sie mit "Bestätigt", "Umbuchen" oder "Stornieren".
              </p>
            </div>
          </div>
        </div>
        {/* SMS reply mockup */}
        <div className="rounded-2xl bg-emerald-100 p-2.5 ml-8 mt-2">
          <p className="text-[9px] text-emerald-900">Bestätigt 👍</p>
        </div>
      </div>
    </PhoneFrame>
  )
}

// ============================================================
// 14. Stats / Dashboard tiles
// ============================================================
export function MockDashboardStats() {
  return (
    <BrowserFrame label="rezaro.de/restaurant/dashboard">
      <div className="bg-pub-bg p-5">
        <p className="text-[11px] uppercase tracking-widest text-pub-muted mb-1">Diese Woche</p>
        <p className="text-[14px] font-semibold text-pub-ink mb-4">Übersicht</p>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="rounded-xl bg-pub-surface border border-pub-rule p-4">
            <p className="text-[9px] uppercase tracking-widest text-pub-muted">Reservierungen</p>
            <p className="text-[24px] font-bold text-pub-ink mt-1">184</p>
            <p className="text-[10px] text-emerald-600 font-semibold">↑ +12 % vs. letzte Woche</p>
          </div>
          <div className="rounded-xl bg-pub-surface border border-pub-rule p-4">
            <p className="text-[9px] uppercase tracking-widest text-pub-muted">Bestellungen</p>
            <p className="text-[24px] font-bold text-pub-ink mt-1">62</p>
            <p className="text-[10px] text-emerald-600 font-semibold">↑ +8 % vs. letzte Woche</p>
          </div>
        </div>
        <div className="rounded-xl bg-pub-surface border border-pub-rule p-4">
          <p className="text-[9px] uppercase tracking-widest text-pub-muted mb-2">Auslastung pro Tag</p>
          <div className="flex items-end gap-1.5 h-16">
            {[40, 55, 35, 75, 90, 95, 80].map((h, i) => (
              <div key={i} className="flex-1 rounded-t" style={{ height: `${h}%`, background: i === 5 ? 'var(--color-pub-accent)' : 'var(--color-pub-accent-bg)' }} />
            ))}
          </div>
          <div className="flex gap-1.5 mt-1.5">
            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((d, i) => (
              <div key={d} className={`flex-1 text-center text-[8px] ${i === 5 ? 'font-bold text-pub-accent' : 'text-pub-muted'}`}>
                {d}
              </div>
            ))}
          </div>
        </div>
      </div>
    </BrowserFrame>
  )
}

// ============================================================
// 15. Volltext-Suche (Owner: search bookings + customers)
// ============================================================
export function MockSearch() {
  const results = [
    { type: 'Reservierung', title: 'Familie Müller — Geburtstag', meta: 'Sa 17.05 · 19:00 · 4 Pers.' },
    { type: 'Gast',         title: 'Müller, Andreas',             meta: '+49 30 1234567 · 8 Besuche · VIP' },
    { type: 'Notiz',        title: 'Allergie: Nüsse, Sesam',      meta: 'Bei Familie Müller hinterlegt' },
    { type: 'Tisch',        title: 'T7 — Fenster',                meta: '4 Plätze · heute frei ab 21:00' },
  ]
  return (
    <BrowserFrame label="rezaro.de/restaurant/suche">
      <div className="bg-pub-bg p-5">
        <div className="mb-4 relative">
          <div className="flex items-center gap-2.5 rounded-xl border-2 border-pub-accent bg-white px-3.5 py-2.5 shadow-[0_0_0_4px_var(--color-pub-accent-bg)]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-pub-accent">
              <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
            </svg>
            <span className="flex-1 text-[12.5px] text-pub-ink">müller</span>
            <span className="rounded bg-pub-bg-soft px-1.5 py-0.5 text-[9px] font-mono text-pub-muted">⌘K</span>
          </div>
          <p className="mt-1.5 text-[10px] text-pub-muted">4 Treffer in 18 ms · alles wird durchsucht</p>
        </div>
        <div className="space-y-2">
          {results.map((r, i) => (
            <div key={i} className="rounded-xl border border-pub-rule bg-white p-3 hover:border-pub-accent transition-colors">
              <div className="flex items-start gap-2">
                <span className="rounded bg-pub-accent-bg px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-pub-accent flex-shrink-0">
                  {r.type}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-semibold text-pub-ink truncate">{r.title}</p>
                  <p className="text-[10.5px] text-pub-muted truncate">{r.meta}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-1">
          {['Datum', 'Status', 'Personen', 'Tisch', 'Notiz'].map(f => (
            <span key={f} className="rounded-full border border-pub-rule bg-pub-surface px-2 py-0.5 text-[10px] text-pub-muted">
              {f}
            </span>
          ))}
        </div>
      </div>
    </BrowserFrame>
  )
}

// ============================================================
// 16. Link teilen — short URL (alternative to QR code)
// ============================================================
export function MockLinkShare() {
  return (
    <BrowserFrame label="rezaro.de/bistro-rosa/widget">
      <div className="bg-pub-bg p-5">
        <p className="text-[11px] uppercase tracking-widest text-pub-muted mb-1">Ihr öffentlicher Link</p>
        <div className="flex items-center gap-2 rounded-xl bg-white border border-pub-rule p-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-pub-accent-bg flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-pub-accent">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12.5px] font-mono text-pub-ink truncate">rezaro.de/<strong>bistro-rosa</strong></p>
            <p className="text-[10px] text-pub-muted">Klicken, kopieren, teilen — fertig.</p>
          </div>
        </div>

        <p className="text-[11px] font-semibold text-pub-ink mb-2">Wo Sie diesen Link einfügen</p>
        <div className="space-y-1.5">
          {[
            { icon: '📷', label: 'Instagram-Bio',      hint: 'Linktree raus, dieser Link rein' },
            { icon: '💬', label: 'WhatsApp-Status',     hint: '„Reservieren" mit einem Tap' },
            { icon: '📘', label: 'Facebook-Seite',      hint: 'Als Aktions-Button "Reservieren"' },
            { icon: '🍽️', label: 'Speisekarte (PDF)',  hint: 'Auf jede Karte als QR oder Fußzeile' },
            { icon: '📧', label: 'E-Mail-Signatur',     hint: 'Jede Mail wird zur Reservierungs-Chance' },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-2.5 rounded-lg bg-white border border-pub-rule px-3 py-2">
              <span className="text-base flex-shrink-0">{s.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="text-[11.5px] font-semibold text-pub-ink">{s.label}</p>
                <p className="text-[10px] text-pub-muted truncate">{s.hint}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </BrowserFrame>
  )
}

// ============================================================
// 17. Anzahlung Konfiguration (Owner)
// ============================================================
export function MockDepositConfig() {
  return (
    <BrowserFrame label="rezaro.de/restaurant/settings/deposit">
      <div className="bg-pub-bg p-5">
        <p className="text-[11px] uppercase tracking-widest text-pub-muted mb-1">Einstellungen · Anzahlungen</p>
        <p className="text-[16px] font-semibold text-pub-ink mb-4">Wann eine Anzahlung verlangen?</p>

        <div className="rounded-xl border border-pub-rule bg-white p-4 mb-3">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[12.5px] font-semibold text-pub-ink">Ab Gruppengröße</p>
            <div className="flex h-5 w-10 rounded-full bg-pub-accent items-center justify-end px-0.5">
              <div className="h-4 w-4 rounded-full bg-white" />
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11.5px]">
            <span className="text-pub-muted">ab</span>
            <span className="rounded-md bg-pub-accent-bg px-2 py-0.5 font-bold text-pub-accent">6 Personen</span>
            <span className="text-pub-muted">— pro Person</span>
            <span className="rounded-md bg-pub-accent-bg px-2 py-0.5 font-bold text-pub-accent">15,00 €</span>
          </div>
        </div>

        <div className="rounded-xl border border-pub-rule bg-white p-4 mb-3">
          <p className="text-[12.5px] font-semibold text-pub-ink mb-2">Spezielle Tage</p>
          <div className="flex flex-wrap gap-1.5">
            {[
              { label: 'Silvester · 31.12', amount: '50 €' },
              { label: 'Valentinstag · 14.02', amount: '20 €' },
              { label: 'Muttertag · 11.05', amount: '15 €' },
              { label: 'Weihnachten · 24.12', amount: '30 €' },
            ].map((d, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-full border border-pub-accent/30 bg-pub-accent-bg px-2 py-0.5 text-[10.5px] text-pub-ink">
                <span className="font-medium">{d.label}</span>
                <span className="font-bold text-pub-accent">· {d.amount}</span>
              </span>
            ))}
          </div>
        </div>

        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
          <p className="text-[11px] font-semibold text-emerald-800">Standard-Reservierungen</p>
          <p className="text-[10.5px] text-emerald-700 mt-0.5">
            Kleinere Gruppen reservieren weiterhin ohne Anzahlung — 0 % Reibung.
          </p>
        </div>
      </div>
    </BrowserFrame>
  )
}

// ============================================================
// 18. Stornierungs-Regeln (Owner)
// ============================================================
export function MockCancelRules() {
  return (
    <BrowserFrame label="rezaro.de/restaurant/settings/cancellation">
      <div className="bg-pub-bg p-5">
        <p className="text-[11px] uppercase tracking-widest text-pub-muted mb-1">Einstellungen · Stornierung</p>
        <p className="text-[16px] font-semibold text-pub-ink mb-4">Klare Regeln, faire Anzahlung</p>

        <div className="space-y-3">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3.5 relative overflow-hidden">
            <div className="absolute right-0 top-0 bottom-0 w-1 bg-emerald-400" />
            <div className="flex items-center justify-between mb-1">
              <p className="text-[12.5px] font-bold text-emerald-900">Bis 24 h vorher</p>
              <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-[9.5px] font-bold text-white">100 % zurück</span>
            </div>
            <p className="text-[10.5px] text-emerald-800 leading-relaxed">
              Vollständige Erstattung der Anzahlung. Kein Stress, kein Anruf — der Gast klickt &quot;Stornieren&quot; im Bestätigungs-E-Mail.
            </p>
          </div>

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5 relative overflow-hidden">
            <div className="absolute right-0 top-0 bottom-0 w-1 bg-amber-400" />
            <div className="flex items-center justify-between mb-1">
              <p className="text-[12.5px] font-bold text-amber-900">12–24 h vorher</p>
              <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[9.5px] font-bold text-white">50 % zurück</span>
            </div>
            <p className="text-[10.5px] text-amber-800 leading-relaxed">
              Teil-Erstattung. Sie behalten 50 % als Aufwandsentschädigung für vorbereiteten Tisch und blockierte Slots.
            </p>
          </div>

          <div className="rounded-xl border border-rose-200 bg-rose-50 p-3.5 relative overflow-hidden">
            <div className="absolute right-0 top-0 bottom-0 w-1 bg-rose-400" />
            <div className="flex items-center justify-between mb-1">
              <p className="text-[12.5px] font-bold text-rose-900">Unter 12 h / No-Show</p>
              <span className="rounded-full bg-rose-500 px-2 py-0.5 text-[9.5px] font-bold text-white">0 % zurück</span>
            </div>
            <p className="text-[10.5px] text-rose-800 leading-relaxed">
              Anzahlung verfällt vollständig. Wird vor der Buchung transparent angezeigt — nichts ist versteckt.
            </p>
          </div>
        </div>

        <p className="mt-3 text-[10.5px] text-pub-muted text-center italic">
          Anzeige der Regeln vor jeder Buchung — Pflicht für DSGVO &amp; faire Praxis.
        </p>
      </div>
    </BrowserFrame>
  )
}

// ============================================================
// 18b. Schichtplanung (Personnel scheduling)
// ============================================================
export function MockShiftSchedule() {
  const days = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
  const employees = [
    { name: 'Anna M.',   role: 'Service',  shifts: [1, 1, 0, 1, 2, 2, 0] },
    { name: 'Marco B.',  role: 'Küche',    shifts: [2, 2, 2, 2, 2, 2, 0] },
    { name: 'Lisa K.',   role: 'Service',  shifts: [0, 1, 1, 0, 1, 2, 1] },
    { name: 'Tarek S.',  role: 'Bar',      shifts: [1, 0, 1, 1, 2, 2, 1] },
    { name: 'Sophie W.', role: 'Service',  shifts: [0, 0, 0, 1, 2, 2, 2] },
  ]
  const shiftStyle = [
    'bg-neutral-100 text-neutral-300',
    'bg-pub-accent-bg text-pub-accent border border-pub-accent/30',
    'bg-pub-accent text-white',
  ]
  const shiftLabel = ['—', 'Mittag', 'Abend']
  return (
    <BrowserFrame label="rezaro.de/bistro-rosa/shift-schedule">
      <div className="bg-pub-bg p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-pub-muted">KW 20 · 12.–18. Mai 2026</p>
            <p className="text-[14px] font-semibold text-pub-ink">Schichtplan</p>
          </div>
          <div className="flex gap-1.5 text-[9px]">
            {shiftStyle.map((s, i) => (
              <span key={i} className={`rounded px-1.5 py-0.5 ${s}`}>{shiftLabel[i]}</span>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-pub-rule bg-white overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-[120px_repeat(7,1fr)] bg-neutral-50 border-b border-pub-rule">
            <div className="px-3 py-1.5 text-[9px] font-semibold text-pub-muted uppercase tracking-widest">Mitarbeiter:in</div>
            {days.map((d, i) => (
              <div key={d} className={`text-center py-1.5 text-[10px] font-semibold ${i === 5 ? 'bg-pub-accent-bg/40 text-pub-accent' : 'text-pub-muted'}`}>
                {d}
              </div>
            ))}
          </div>
          {/* Rows */}
          {employees.map((e, ri) => (
            <div key={e.name} className={`grid grid-cols-[120px_repeat(7,1fr)] ${ri < employees.length - 1 ? 'border-b border-pub-rule/60' : ''}`}>
              <div className="px-3 py-2">
                <p className="text-[11px] font-semibold text-pub-ink truncate">{e.name}</p>
                <p className="text-[9px] text-pub-muted">{e.role}</p>
              </div>
              {e.shifts.map((s, di) => (
                <div key={di} className="p-1">
                  <div className={`h-7 rounded flex items-center justify-center text-[9px] font-semibold ${shiftStyle[s]}`}>
                    {s === 0 ? '' : shiftLabel[s].slice(0, 1)}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="mt-2.5 flex items-center justify-between text-[9.5px]">
          <span className="text-pub-muted">5 Mitarbeitende · 32 Stunden geplant</span>
          <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-emerald-700 font-semibold">Genehmigt</span>
        </div>
      </div>
    </BrowserFrame>
  )
}

// ============================================================
// 19. QR-Menü mit echten Bildern (was Gast sieht)
// ============================================================
export function MockQRMenuRich() {
  const items = [
    { name: 'Burrata mit Kirschtomaten', price: '12,90 €', emoji: '🥗', tag: 'Vegetarisch' },
    { name: 'Tagliatelle al Tartufo',    price: '18,50 €', emoji: '🍝', tag: 'Empfehlung' },
    { name: 'Bistecca Fiorentina',       price: '34,00 €', emoji: '🥩', tag: 'Klassiker' },
    { name: 'Tiramisù della Nonna',      price: '6,50 €',  emoji: '🍰' },
  ]
  return (
    <PhoneFrame>
      <div className="h-full bg-pub-bg overflow-y-auto">
        <div className="bg-gradient-to-br from-pub-accent to-indigo-700 px-4 pt-9 pb-5 text-white">
          <p className="text-[10px] uppercase tracking-widest opacity-80">Tisch 7 · Bistro Rosa</p>
          <p className="text-[16px] font-bold mt-1">Speisekarte</p>
          <div className="mt-3 flex gap-1.5">
            {['Vorspeisen', 'Pasta', 'Hauptgang', 'Dessert'].map((c, i) => (
              <span key={i} className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${i === 1 ? 'bg-white text-pub-accent' : 'bg-white/15 text-white'}`}>
                {c}
              </span>
            ))}
          </div>
        </div>

        <div className="p-3 space-y-2.5">
          {items.map((it, i) => (
            <div key={i} className="rounded-2xl bg-white border border-pub-rule p-2.5 flex gap-2.5">
              {/* Image placeholder with emoji */}
              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-pub-accent-bg to-pub-bg-soft text-3xl">
                {it.emoji}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[12px] font-semibold text-pub-ink leading-tight">{it.name}</p>
                  <p className="text-[12px] font-bold text-pub-accent flex-shrink-0">{it.price}</p>
                </div>
                {it.tag && (
                  <span className="mt-1 inline-block rounded bg-pub-accent-bg px-1.5 py-0.5 text-[9px] font-semibold text-pub-accent">
                    {it.tag}
                  </span>
                )}
                <button className="mt-2 w-full rounded-lg bg-pub-ink py-1.5 text-[10.5px] font-semibold text-white">
                  + Hinzufügen
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 m-3 rounded-xl bg-pub-accent p-3 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest opacity-80">Warenkorb · 2 Artikel</p>
              <p className="text-[14px] font-bold mt-0.5">31,40 €</p>
            </div>
            <span className="text-[11px] font-semibold">Bezahlen →</span>
          </div>
        </div>
      </div>
    </PhoneFrame>
  )
}

// ============================================================
// 20. Hero composition (used on homepage / top pages)
// ============================================================
export function HeroComposition({ primary }: { primary: 'reservation' | 'order' | 'menu' | 'website' }) {
  const map = {
    reservation: <MockReservationBook />,
    order: <MockOrders />,
    menu: <MockQRMenu />,
    website: <MockRestaurantWebsite />,
  }
  return (
    <div className="relative">
      <div className="relative z-10">{map[primary]}</div>
      {/* Decorative glow */}
      <div className="absolute -inset-12 -z-10 rounded-full opacity-40"
        style={{ background: 'radial-gradient(circle, var(--color-pub-accent-bg) 0%, transparent 60%)' }} />
    </div>
  )
}
